// useContractAdmin.js
import { useState, useEffect, useContext, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import { ModalContext } from "contexts/Modal";

const emptyInputs = {
  agreement: "",
  datesStart: "",
  datesEnd: "",
  scopes: [],
};

const ensureAuthed = async () => {
  await Auth.currentAuthenticatedUser();
};

async function fetchContract(contractId, setContract, setInputs, reloadContract, setScopesValidity) {
  await ensureAuthed();
  try {
    const contractData = await API.get("AccountGatewayApi", `/contract/${contractId}`);
    setContract(contractData);
    setInputs({
      agreement: contractData.agreement || "",
      datesStart: contractData.dates?.start ? new Date(contractData.dates.start) : null,
      datesEnd: contractData.dates?.end ? new Date(contractData.dates.end) : null,
      scopes: contractData.scopes || [],
    });
    setScopesValidity((contractData.scopes || []).map(() => true));
    reloadContract(false);
  } catch (err) {
    console.error("error fetching contract", err);
    reloadContract(false);
  }
}

async function patchContract(contractId, patchObj, setContract, updateContract, showModal, setIsSaving) {
  await ensureAuthed();
  try {
    setIsSaving(true);
    const updated = await API.patch("AccountGatewayApi", `/contract/${contractId}`, {
      body: patchObj,
    });
    setContract(updated);
    updateContract(false);
    setIsSaving(false);
    showModal("Contract save successful", "Contract updated.");
  } catch (err) {
    console.log("error updating contract", err);
    updateContract(false);
    setIsSaving(false);
    showModal("Contract save unsuccessful", "Error occurred while saving contract.");
  }
}

function validateInputs(inputs, showModal) {
  if (inputs.datesStart && inputs.datesEnd) {
    if (inputs.datesStart.valueOf() > inputs.datesEnd.valueOf()) {
      showModal("Save Account", "End date must be after start date.");
      return false;
    }
  }
  return true;
}

const useContractAdmin = (contractId) => {
  const [contract, setContract] = useState({});
  const [inputs, setInputs] = useState(emptyInputs);
  const [shouldReloadContract, reloadContract] = useState(false);
  const [shouldUpdateContract, updateContract] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [scopesValidity, setScopesValidity] = useState([]);
  const { showModal } = useContext(ModalContext);

  // Auth readiness
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setContract({});
          setInputs(emptyInputs);
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // Prevent duplicate fetches (StrictMode)
  const fetchInFlight = useRef(false);

  // Load contract when authed and id present
  useEffect(() => {
    if (!ready || !contractId) return;
    if (fetchInFlight.current) return;
    fetchInFlight.current = true;

    let cancelled = false;
    (async () => {
      await fetchContract(
        contractId,
        (v) => !cancelled && setContract(v),
        (v) => !cancelled && setInputs(v),
        (v) => !cancelled && reloadContract(v),
        (v) => !cancelled && setScopesValidity(v)
      );
      fetchInFlight.current = false;
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, contractId, shouldReloadContract]);

  // Save changes when flagged, only if authed
  const saveInFlight = useRef(false);
  useEffect(() => {
    if (!ready || !contractId || !shouldUpdateContract) return;
    if (saveInFlight.current) return;
    saveInFlight.current = true;

    const patchObj = {
      agreement: inputs.agreement ?? contract?.agreement ?? "",
      dates: {
        start: inputs.datesStart ?? contract?.dates?.start ?? null,
        end: inputs.datesEnd ?? contract?.dates?.end ?? null,
      },
      scopes: inputs.scopes ?? contract?.scopes ?? [],
    };

    (async () => {
      await patchContract(contractId, patchObj, setContract, updateContract, showModal, setIsSaving);
      saveInFlight.current = false;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, shouldUpdateContract, contractId]); // (inputs are read when flag turns true)

  function handleSave(event) {
    if (event) event.preventDefault();
    try {
      if (!validateInputs(inputs, showModal)) return;
      updateContract(true);
    } catch (err) {
      console.error("ContractAdmin-logic.js: Error occurred while saving contract.", err);
      showModal("Save Contract", "Error occurred while saving contract.");
    }
  }

  return {
    contract,
    reloadContract,
    updateContract,
    inputs,
    setInputs,
    handleSave,
    isSaving,
    scopesValidity,
    setScopesValidity,
  };
};

export default useContractAdmin;
